/**
 * React Query hooks for `/api/context-variables` — the workspace-scoped
 * catalog of declared `{{context.*}}` variables (context-variables unit,
 * PR-C). The REST surface speaks camelCase (its DTO contract), matching the
 * dashboard types, so no key transform is needed — bodies and responses pass
 * through the shared `apiRequest` verbatim (mirrors `agents/api/variables.ts`).
 *
 * Contract: see `aidlc-docs/construction/context-variables/functional-design/contracts.md` § 5.1.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest } from "../../agents/api/client";

export type ContextVariableType = "string" | "number" | "boolean" | "enum";

export interface WorkspaceContextVariable {
  id: string;
  key: string;
  label: string;
  description?: string;
  type: ContextVariableType;
  required: boolean;
  sensitive: boolean;
  includeInPrompt: boolean;
  enumValues: string[];
  exampleValue?: string;
}

export interface CreateContextVariableInput {
  key: string;
  label: string;
  type: ContextVariableType;
  required?: boolean;
  sensitive?: boolean;
  includeInPrompt?: boolean;
  enumValues?: string[];
  description?: string;
  exampleValue?: string;
}

/** PATCH never changes `key`. */
export type UpdateContextVariableInput = Partial<Omit<CreateContextVariableInput, "key">>;

const PATH = "/api/context-variables";

export const contextVariableKeys = {
  all: (slug: string) => ["context-variables", slug] as const,
  list: (slug: string) => ["context-variables", slug, "list"] as const,
};

export function useContextVariables(slug: string) {
  return useQuery({
    queryKey: contextVariableKeys.list(slug),
    // The controller wraps the list in `{ items: [...] }`; unwrap so consumers
    // get a bare array (both the settings table and the @-mention picker map it).
    queryFn: () =>
      apiRequest<{ items: WorkspaceContextVariable[] }>("GET", PATH).then((r) => r.items),
  });
}

export function useCreateContextVariable(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateContextVariableInput) =>
      apiRequest<WorkspaceContextVariable>("POST", PATH, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contextVariableKeys.all(slug) });
    },
  });
}

export function useUpdateContextVariable(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateContextVariableInput }) =>
      apiRequest<WorkspaceContextVariable>("PATCH", `${PATH}/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contextVariableKeys.all(slug) });
    },
  });
}

export function useDeleteContextVariable(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest<void>("DELETE", `${PATH}/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contextVariableKeys.all(slug) });
    },
  });
}
